import React from "react";
import { Helmet } from 'react-helmet';
import Hero from "./hero";
import ServiceCards from "./serviceCards";
import Ecosystem from "./ecosystem";
import Engagement from "./engagement";

const servicesFaqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
        {
            "@type": "Question",
            "name": "What services does Partnered Management Group offer?",
            "acceptedAnswer": {
                "@type": "Answer",
                "text": "PMG offers four core fractional services: Fractional Project Management (PMP-certified PMs embedded as white-labeled team members), PMO Services via ReadyPMO (a turnkey Project Management Office stood up in 4–6 weeks), Project Management Assessment (a deep audit with tailored future-state strategy and implementation plan), and Automation Workflow Development (custom automations that reduce manual effort and integrate with existing tools). All engagements are tailored and delivered by senior practitioners."
            }
        },
        {
            "@type": "Question",
            "name": "How is PMG's fractional project management different from traditional consulting?",
            "acceptedAnswer": {
                "@type": "Answer",
                "text": "PMG practitioners are hands-on executors, not advisors. They embed directly into your team, own day-to-day delivery, and act as a white-labeled extension of your organization. Unlike traditional consultants who provide recommendations and exit, PMG's fractional PMs take accountability for timelines, communication, and outcomes — at a cost 30% less than a full-time W-2 hire, with no benefits overhead."
            }
        },
        {
            "@type": "Question",
            "name": "Can a small business afford fractional project management services?",
            "acceptedAnswer": {
                "@type": "Answer",
                "text": "Yes. PMG's fractional model was designed specifically for small to mid-sized businesses that need senior-level execution without the overhead of a full-time hire. PMG's services are 30% less expensive than hiring a W-2 employee, and clients have saved over $1.2 million in combined labor expenses. Engagements are month-to-month after a 2-month minimum, making them accessible and low-risk for growing businesses."
            }
        }
    ]
};

const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
        {
            "@type": "ListItem",
            "position": 1,
            "name": "Home",
            "item": "https://www.partneredmg.com/"
        },
        {
            "@type": "ListItem",
            "position": 2,
            "name": "Fractional Services",
            "item": "https://www.partneredmg.com/services"
        }
    ]
};

const servicesPageSchema = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    "@id": "https://www.partneredmg.com/services#webpage",
    "url": "https://www.partneredmg.com/services",
    "name": "Fractional PM Services | Project Management, PMO & Automation | PMG",
    "description": "PMG offers fractional project management, PMO services, project management assessments, and automation workflow development. PMP-certified practitioners for small and mid-sized businesses.",
    "isPartOf": { "@id": "https://www.partneredmg.com/#website" },
    "about": { "@id": "https://www.partneredmg.com/#organization" },
    "breadcrumb": { "@id": "https://www.partneredmg.com/services#breadcrumb" }
};

const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "name": "Partnered Management Group — Fractional Services",
    "description": "Three fractional specialties from PMG: fractional project management, fractional operations, and fractional design & development. Hire one, add more as you grow.",
    "provider": { "@id": "https://www.partneredmg.com/#organization" },
    "itemListElement": [
        {
            "@type": "ListItem",
            "position": 1,
            "item": {
                "@type": "Service",
                "name": "Fractional Project Management",
                "description": "A senior, PMP-certified project manager embedded with your team to own delivery from kickoff to close — without the full-time overhead.",
                "provider": { "@id": "https://www.partneredmg.com/#organization" },
                "serviceType": "Fractional Project Management",
                "areaServed": { "@type": "Country", "name": "United States" },
                "url": "https://www.partneredmg.com/services",
                "offers": {
                    "@type": "Offer",
                    "priceCurrency": "USD",
                    "priceRange": "$4,600–$12,500/month",
                    "description": "Fractional project management retainer starting at $4,600/month for 10 hours/week. Month-to-month after a 2-month minimum commitment.",
                    "seller": { "@id": "https://www.partneredmg.com/#organization" }
                }
            }
        },
        {
            "@type": "ListItem",
            "position": 2,
            "item": {
                "@type": "Service",
                "name": "Fractional Operations",
                "description": "Senior operations leadership that designs the operating model, SOPs, and tool stack your business runs on.",
                "provider": { "@id": "https://www.partneredmg.com/#organization" },
                "serviceType": "Fractional Operations Management",
                "areaServed": { "@type": "Country", "name": "United States" },
                "url": "https://www.partneredmg.com/services"
            }
        },
        {
            "@type": "ListItem",
            "position": 3,
            "item": {
                "@type": "Service",
                "name": "Fractional Design & Development",
                "description": "A design-led product team you can borrow — brand and product design plus front-end and full-stack engineering, organized into pods you can spin up and down.",
                "provider": { "@id": "https://www.partneredmg.com/#organization" },
                "serviceType": "Fractional Design and Development",
                "areaServed": { "@type": "Country", "name": "United States" },
                "url": "https://www.partneredmg.com/services"
            }
        }
    ]
};

const Services = () => {
    return (
        <div className="w-full">
            <Helmet>
                <title>Fractional Services | Fractional PM, Operations & Design + Dev | PMG</title>
                <meta name="description" content="PMG's fractional services: senior, PMP-certified project management, fractional operations, and fractional design & development. Hire one specialty, add a second when you feel it. Embedded in 1–2 weeks." />
                <link rel="canonical" href="https://www.partneredmg.com/services" />
                <meta property="og:title" content="Fractional Services | Fractional PM, Operations & Design + Dev | PMG" />
                <meta property="og:description" content="Three fractional specialties — project management, operations, and design & development. Senior practitioners embedded with your team, shaped around your work." />
                <meta property="og:url" content="https://www.partneredmg.com/services" />
                <meta property="og:type" content="website" />
                <script type="application/ld+json">{JSON.stringify(servicesPageSchema)}</script>
                <script type="application/ld+json">{JSON.stringify(servicesFaqSchema)}</script>
                <script type="application/ld+json">{JSON.stringify(serviceSchema)}</script>
                <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
            </Helmet>

            <Hero />
            <ServiceCards />
            <Ecosystem />
            <Engagement />
        </div>
    );
}

export default Services;
